import { useState, useEffect, useRef, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';
import { getMyInquiryDetail, sendCustomerMessage } from '../api/inquiry.api';
import { toast } from './Toast';

const formatTime = (d) =>
    new Date(d).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }) +
    ' · ' +
    new Date(d).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });

export default function InquiryChat({ inquiryNumber, isClosed = false }) {
    const { socket } = useSocket();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    // Load initial messages from REST
    const loadMessages = useCallback(async () => {
        try {
            const res = await getMyInquiryDetail(inquiryNumber);
            if (res.data?.isOk) {
                setMessages(res.data.data.messages || []);
            }
        } catch {
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, [inquiryNumber]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    // Join socket room and listen for real-time messages
    useEffect(() => {
        if (!socket) return;

        socket.emit('join:inquiry', { inquiryNumber });

        const handleIncoming = ({ inquiryNumber: inq, message }) => {
            if (inq !== inquiryNumber) return;
            setMessages((prev) => {
                // Deduplicate by _id in case the sender already optimistically added it
                if (prev.some((m) => m._id && m._id === message._id)) return prev;
                return [...prev, message];
            });
        };

        socket.on('inquiry:message', handleIncoming);

        return () => {
            socket.off('inquiry:message', handleIncoming);
            socket.emit('leave:inquiry', { inquiryNumber });
        };
    }, [socket, inquiryNumber]);

    // Scroll to bottom when messages change
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        const trimmed = text.trim();
        if (!trimmed || sending) return;

        setSending(true);
        // Optimistic add
        const optimistic = {
            _id: `opt-${Date.now()}`,
            senderType: 'customer',
            senderName: 'You',
            message: trimmed,
            sentAt: new Date().toISOString(),
            optimistic: true,
        };
        // setMessages((prev) => [...prev, optimistic]);
        setText('');

        try {
            const res = await sendCustomerMessage(inquiryNumber, { message: trimmed });
            if (res.data?.isOk && res.data.data) {
                // Replace the optimistic entry with the real saved message so the
                // socket broadcast (which carries the same _id) is deduped correctly.
                setMessages((prev) =>
                    prev.map((m) => (m._id === optimistic._id ? res.data.data : m))
                );
            }
        } catch {
            // Roll back optimistic message
            setMessages((prev) => prev.filter((m) => m._id !== optimistic._id));
            setText(trimmed);
            toast.error('Failed to send message');
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40">
                <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Message list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
                {messages.length === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-8">
                        No messages yet. Send one to start the conversation.
                    </p>
                )}
                {messages.map((msg, i) => {
                    const isCustomer = msg.senderType === 'customer';
                    return (
                        <div key={msg._id || i} className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] ${isCustomer ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                                {!isCustomer && (
                                    <span className="text-xs text-muted-foreground px-1">
                                        {msg.senderName || 'Support'}
                                    </span>
                                )}
                                <div
                                    className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                                        isCustomer
                                            ? 'bg-primary text-primary-foreground rounded-br-sm'
                                            : 'bg-muted text-foreground rounded-bl-sm'
                                    } ${msg.optimistic ? 'opacity-70' : ''}`}
                                >
                                    {msg.message}
                                </div>
                                <span className="text-xs text-muted-foreground px-1">
                                    {formatTime(msg.sentAt)}
                                </span>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            {/* Input */}
            {!isClosed && (
                <div className="flex-shrink-0 border-t border-border px-3 py-3 flex gap-2 items-end">
                    <textarea
                        ref={inputRef}
                        rows={1}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message… (Enter to send)"
                        className="flex-1 border border-border rounded-xl px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                        style={{ maxHeight: 100 }}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!text.trim() || sending}
                        className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {sending
                            ? <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                            : <i className="ri-send-plane-fill text-sm"></i>
                        }
                    </button>
                </div>
            )}
            {isClosed && (
                <p className="flex-shrink-0 text-center text-xs text-muted-foreground py-3 border-t border-border">
                    This inquiry is closed. No new messages can be sent.
                </p>
            )}
        </div>
    );
}
